import { Fragment, useRef, type ReactNode } from "react"

import { cn } from "../lib/cn"

export type ItemDeSubmenu<C extends string> = {
  chave: C
  rotulo: string
  contagem?: number | null
  dica?: string
  grupo?: string
  desenhar: () => ReactNode
}

export function PainelComSubmenu<C extends string>({
  nome,
  rotulo,
  itens,
  atual,
  aoTrocar,
  abertos,
}: {
  nome: string
  rotulo: string
  itens: ItemDeSubmenu<C>[]
  atual: C
  aoTrocar: (chave: C) => void
  abertos: ReadonlySet<C>
}) {
  const botoes = useRef<Map<C, HTMLButtonElement>>(new Map())

  const irPara = (indice: number) => {
    const alvo = itens[((indice % itens.length) + itens.length) % itens.length]
    if (!alvo) return
    aoTrocar(alvo.chave)
    botoes.current.get(alvo.chave)?.focus()
  }

  const aoTeclar = (evento: React.KeyboardEvent<HTMLButtonElement>) => {
    const posicao = itens.findIndex((i) => i.chave === atual)
    const acoes: Record<string, () => void> = {
      ArrowDown: () => irPara(posicao + 1),
      ArrowUp: () => irPara(posicao - 1),
      Home: () => irPara(0),
      End: () => irPara(itens.length - 1),
    }
    const acao = acoes[evento.key]
    if (acao) {
      evento.preventDefault()
      acao()
    }
  }

  return (
    <div className="grid grid-cols-1 items-stretch gap-4 py-3 min-[901px]:grid-cols-[232px_minmax(0,1fr)]">
      <nav
        className="sticky top-3 flex flex-row flex-wrap gap-0.5 border-r border-(--borda) bg-card min-[901px]:flex-col min-[901px]:flex-nowrap"
        role="tablist"
        aria-orientation="vertical"
        aria-label={rotulo}
      >
        {itens.map((item, indice) => {
          const ativo = item.chave === atual
          const abreGrupo =
            item.grupo !== undefined && item.grupo !== itens[indice - 1]?.grupo
          return (
            <Fragment key={item.chave}>
            {abreGrupo && (
              <span
                className="block pt-3 pr-3 pb-0.5 pl-[calc(var(--esp-3)+3px)] text-(length:--texto-sm) font-semibold text-muted-foreground first:pt-1 max-[901px]:basis-full"
                aria-hidden="true"
              >
                {item.grupo}
              </span>
            )}
            <button
              type="button"
              role="tab"
              id={`submenu-${nome}-${item.chave}`}
              aria-label={item.grupo !== undefined ? `${item.grupo} · ${item.rotulo}` : undefined}
              aria-selected={ativo}
              aria-controls={`bloco-${nome}-${item.chave}`}
              tabIndex={ativo ? 0 : -1}
              className={cn(
                "mx-1 flex w-[calc(100%-2*var(--esp-1))] cursor-pointer items-baseline gap-2 rounded-(--raio-2) border py-2 pr-[calc(var(--esp-3)-var(--esp-1)-1px)] pl-[calc(var(--esp-3)-var(--esp-1)+2px)] text-left text-(length:--texto-lg) focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                ativo
                  ? "border-(--borda) bg-(--painel-alt) font-semibold text-(--nav-tinta-ativa)"
                  : "border-transparent bg-transparent font-medium text-muted-foreground hover:bg-(--painel-alt) hover:text-foreground",
              )}
              ref={(elemento) => {
                if (elemento === null) botoes.current.delete(item.chave)
                else botoes.current.set(item.chave, elemento)
              }}
              onClick={() => aoTrocar(item.chave)}
              onKeyDown={aoTeclar}
            >
              <span className="mr-auto">{item.rotulo}</span>
              {item.contagem !== null && item.contagem !== undefined && (
                <span
                  className={cn(
                    "text-(length:--texto-sm) font-normal tabular-nums",
                    ativo ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {item.contagem.toLocaleString("pt-BR")}
                </span>
              )}
            </button>
            </Fragment>
          )
        })}
      </nav>

      <div className="flex min-w-0 flex-col gap-4 pt-3 [&_[data-slot=card]]:border-0 [&_[data-slot=card]]:pt-0">
        {itens
          .filter((item) => abertos.has(item.chave))
          .map((item) => (
            <div
              key={item.chave}
              className="flex flex-col gap-4 [&[hidden]]:hidden"
              role="tabpanel"
              id={`bloco-${nome}-${item.chave}`}
              aria-labelledby={`submenu-${nome}-${item.chave}`}
              hidden={item.chave !== atual}
              tabIndex={item.chave === atual ? 0 : -1}
            >
              {item.desenhar()}
            </div>
          ))}
      </div>
    </div>
  )
}
